import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Button, Form, Select, Space} from 'antd';
import {
  StyledBudgetDetailsForm,
  StyledBudgetDetailsFormContainer,
  StyledBudgetDetailsFormSpace
} from './StyledBudgetDetailsForm';
import EuroInput from '../../../EuroInput';
import Spin from '../../../Spin';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {SCAActionsConstants, subscriptionFormFields} from '../../../../constants';
import {BillOptions, scaHelpers} from '../../../../utils/helpers';
import {subscriptionActions} from '../../../../state/actions/subscription.actions';

const {Item} = Form;

const {billedFieldName, budgetLimitFieldName} = subscriptionFormFields;

const initialFormValues = {
  [billedFieldName]: '',
  [budgetLimitFieldName]: ''
};

const operationName = SCAActionsConstants.SUBSCRIPTION_EDIT_ACTION;

const BudgetDetailsForm = ({
  onCancel,
  onSuccess,
  subscription,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [form] = Form.useForm();
  const [billOptions, ] = useState(BillOptions());
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const trans = (key) => t(`subscriptions:${key}`);

  useEffect(() => {
    const fieldValues = {
      ...initialFormValues,
      [billedFieldName]: subscription?.[billedFieldName],
      [budgetLimitFieldName]: subscription?.[budgetLimitFieldName] || 0
    };
    form.setFieldsValue(fieldValues);
  }, [subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  const requiredErrorMessage = t('validation.fieldIsRequired');

  const SCAErrorCallback = ({response, successCallback, errorCallback}) => {
    scaHelpers.SCAResponseCallback({
      response,
      scaCallback: (scaProps) => {
        setAuthWindowProps({...authWindowProps, ...scaProps});
        successCallback && successCallback();
      },
      errorCallback
    });
  }

  const handleOnCancel = (e) => onCancel?.(e);

  const handleOnSave = () => form.submit();

  const handleSubmit = (data) => {
    setLoading(true);

    dispatch(subscriptionActions.updateSubscription({
      id: subscription.id,
      data,
      headers: scaHelpers.getAuthHeaders(operationName),
      errorCallback: (response) => {
        SCAErrorCallback({
          response,
          errorCallback: () => setLoading(false),
        });
      },
      successCallback: (response) => {
        setLoading(false);
        onSuccess?.(response);
      }
    }));
  }

  const handleCancelAuthModal = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    if (loading) setLoading(false);
  }

  return (
    <StyledBudgetDetailsForm {...rest}>
      <Spin spinning={loading} size='xsmall'>
        <StyledBudgetDetailsFormContainer>
          <Form
            initialValues={initialFormValues}
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <StyledBudgetDetailsFormSpace size='small' align='start'>
              <Item
                label={trans('budgetValue')}
                name={budgetLimitFieldName}
                required
              >
                <EuroInput size='large' />
              </Item>
              <Item
                label=' '
                name={billedFieldName}
                rules={[{required: true, message: requiredErrorMessage}]}
              >
                <Select options={billOptions} size='large' />
              </Item>
            </StyledBudgetDetailsFormSpace>
          </Form>
          <Space direction='horizontal' size='small'>
            <Button onClick={handleOnCancel}>
              {t('cancel')}
            </Button>
            <Button type='primary' onClick={handleOnSave}>
              {t('save')}
            </Button>
          </Space>
        </StyledBudgetDetailsFormContainer>
      </Spin>

      <AuthenticationWindow
        {...authWindowProps}
        handleCancel={handleCancelAuthModal}
        onSuccess={handleSubmit}
        operationName={operationName}
      />

    </StyledBudgetDetailsForm>
  );
}

BudgetDetailsForm.propTypes = {
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  subscription: PropTypes.object,
}

export default BudgetDetailsForm;
